/**
 * Generic ranking extraction (§4.8.2, FR-46 – FR-49).
 *
 * The key insight this whole module rests on: the tool already holds the
 * complete Sleeper player universe, so it can **validate** a candidate
 * extraction instead of guessing at one. Every candidate block is parsed, its
 * rows are matched against the real NFL player list, and the fraction that
 * resolve *is* the confidence score. A block of navigation links scores near
 * zero; a top-200 ranking table scores near one. No selector heuristics
 * required, and no site-specific knowledge.
 *
 * The same principle handles column roles: a column that is 90% valid NFL team
 * abbreviations is the team column no matter what its header says — which is
 * what makes this work on a spreadsheet paste with no headers at all.
 *
 * Pure module — no I/O, no DOM. Runs identically in the browser, a Worker, and
 * Node tests.
 */

import type { PlayerId, Position } from '../types.ts';
import { PlayerIndex, normalizePosition, normalizeTeam, stripAnnotations } from '../matching.ts';
import { decodeEntities, htmlToText, looksLikeHtml, normalizeWhitespace, stripDangerousBlocks } from './sanitize.ts';

export type ColumnRole = 'rank' | 'name' | 'team' | 'position' | 'tier' | 'bye' | 'notes' | 'ignore';

export interface ExtractedRow {
  rank: number;
  name: string;
  team: string | null;
  position: Position | null;
  tier: number | null;
  bye: number | null;
  notes: string | null;
  /** Resolved Sleeper id, or null if the name did not match. */
  player_key: PlayerId | null;
  /** Fuzzy candidates when unmatched (FR-49). */
  suggestions: { player_key: PlayerId; full_name: string; score: number }[];
}

export interface CandidateBlock {
  /** How the block was found, for the recipe (FR-50). */
  strategy: 'html-table' | 'html-list' | 'text-numbered' | 'text-delimited' | 'json-embedded';
  /** Index of this block among blocks found by the same strategy. */
  index: number;
  /** Raw cell grid before column roles are assigned. */
  cells: string[][];
  /** Inferred role per column. */
  columns: ColumnRole[];
  /** Rows that resolved to a real NFL player / total rows (FR-46). */
  confidence: number;
  rows: ExtractedRow[];
  matched: number;
  total: number;
  /** True when a header row was detected and dropped. */
  had_header: boolean;
}

export interface ExtractionResult {
  best: CandidateBlock | null;
  /** All candidates, best first — the preview offers these as alternatives. */
  candidates: CandidateBlock[];
  /** Rejected because confidence was below the threshold (FR-46). */
  rejected: boolean;
  threshold: number;
}

export interface ExtractOptions {
  threshold?: number;
  /** Fuzzy match floor passed through to the matcher. */
  matchThreshold?: number;
  /** Force a column mapping (recipe replay, or a user remap in the preview). */
  columns?: ColumnRole[];
  /** Force a strategy + index (recipe replay). */
  strategy?: CandidateBlock['strategy'];
  strategyIndex?: number;
  /** Rows shorter than this are not a ranking list. */
  minRows?: number;
}

const DEFAULT_MIN_ROWS = 10;

/**
 * Extract a ranking list from arbitrary input.
 *
 * Accepts HTML or plain text and decides which by content, because a user
 * pasting "select all" from a browser gets text while "view source" gets HTML,
 * and neither should have to say which they did.
 */
export function extractRankings(
  input: string,
  index: PlayerIndex,
  options: ExtractOptions = {},
): ExtractionResult {
  const threshold = options.threshold ?? 0.7;
  const minRows = options.minRows ?? DEFAULT_MIN_ROWS;

  const blocks: CandidateBlock[] = [];
  const isHtml = looksLikeHtml(input);

  if (isHtml) {
    const safe = stripDangerousBlocks(input);
    blocks.push(...findHtmlTables(safe, minRows));
    blocks.push(...findHtmlLists(safe, minRows));
    // Even for HTML, run the text pipeline over the rendered text: it catches
    // "repeated sibling pattern" layouts (div-per-player) that are not tables
    // or lists, which regex-scanning the markup would miss entirely.
    blocks.push(...findTextBlocks(htmlToText(input), minRows));
    // Deliberately scans the *raw*, unsanitized input, not `safe` — the data
    // this looks for lives inside <script> tags that stripDangerousBlocks just
    // discarded on purpose (FR-56). That is still the right call for markup:
    // this function never treats script content as markup. It only ever
    // accepts a strictly `JSON.parse`-able literal, so nothing here can
    // execute, and every value it produces flows into the same
    // content-validated scoring as any other strategy — a script stuffed with
    // fake names still has to match the real player index to win.
    blocks.push(...findEmbeddedJson(input, minRows));
  } else {
    blocks.push(...findTextBlocks(normalizeWhitespace(input), minRows));
  }

  let candidates = blocks;
  if (options.strategy) {
    candidates = candidates.filter(
      (b) => b.strategy === options.strategy &&
        (options.strategyIndex === undefined || b.index === options.strategyIndex),
    );
  }

  for (const block of candidates) {
    block.columns = options.columns ?? inferColumns(block.cells, index);
    scoreBlock(block, index, options.matchThreshold ?? 0.86);
  }

  candidates.sort((a, b) => {
    // Confidence first, then size — between two clean extractions, the longer
    // list is the ranking and the shorter one is a sidebar.
    if (Math.abs(b.confidence - a.confidence) > 0.02) return b.confidence - a.confidence;
    return b.matched - a.matched;
  });

  const best = candidates[0] ?? null;
  const rejected = !best || best.confidence < threshold;

  return { best, candidates: candidates.slice(0, 5), rejected, threshold };
}

// ---------------------------------------------------------------------------
// Candidate block detection
// ---------------------------------------------------------------------------

function findHtmlTables(html: string, minRows: number): CandidateBlock[] {
  const out: CandidateBlock[] = [];
  const tables = html.match(/<table\b[^>]*>[\s\S]*?<\/table\s*>/gi) ?? [];

  tables.forEach((table, index) => {
    const rows = table.match(/<tr\b[^>]*>[\s\S]*?<\/tr\s*>/gi) ?? [];
    const cells = rows
      .map((row) => {
        const tds = row.match(/<t[dh]\b[^>]*>[\s\S]*?<\/t[dh]\s*>/gi) ?? [];
        return tds.map(cellText);
      })
      .filter((r) => r.length > 0);
    if (cells.length >= minRows) {
      out.push(emptyBlock('html-table', index, cells));
    }
  });

  return out;
}

function findHtmlLists(html: string, minRows: number): CandidateBlock[] {
  const out: CandidateBlock[] = [];
  const lists = html.match(/<(ol|ul)\b[^>]*>[\s\S]*?<\/\1\s*>/gi) ?? [];

  lists.forEach((list, index) => {
    const items = list.match(/<li\b[^>]*>[\s\S]*?<\/li\s*>/gi) ?? [];
    const cells = items.map((item) => splitTextRow(cellText(item)));
    if (cells.length >= minRows) {
      out.push(emptyBlock('html-list', index, cells));
    }
  });

  return out;
}

// Bounds on the work done per candidate — a page is untrusted input, and none
// of these numbers are reachable by any real ranking page (the largest known
// case, a 500-player consensus board, is well under all of them).
const MAX_SCRIPT_BYTES = 8_000_000;
const MAX_LITERAL_BYTES = 6_000_000;
const MAX_EQUALS_SCANNED = 3_000;
const MAX_CANDIDATE_LITERALS = 20;
const MAX_ARRAYS_INSPECTED = 200;
const MAX_TRAVERSAL_DEPTH = 6;
const MAX_JSON_COLUMNS = 30;

/**
 * Find ranking data embedded as a literal JSON blob inside a `<script>` tag.
 *
 * Some pages render their table client-side from data the server already sent
 * as inline JSON (`var ecrData = {...};`) rather than as markup — the data is
 * present in the raw response, it is just not yet shaped into a `<table>` a
 * DOM-based extractor could find. This looks for that JSON directly instead of
 * waiting for markup that a client-side script produces.
 */
function findEmbeddedJson(html: string, minRows: number): CandidateBlock[] {
  const out: CandidateBlock[] = [];
  const scripts = html.match(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi) ?? [];

  for (const script of scripts) {
    if (script.length > MAX_SCRIPT_BYTES) continue;
    const inner = script.replace(/^<script\b[^>]*>/i, '').replace(/<\/script\s*>$/i, '');

    let candidates = 0;
    let attempts = 0;
    let searchFrom = 0;
    while (attempts < MAX_EQUALS_SCANNED && candidates < MAX_CANDIDATE_LITERALS) {
      const eq = inner.indexOf('=', searchFrom);
      if (eq === -1) break;
      searchFrom = eq + 1;
      attempts++;

      // Skip `==`, `===`, `=>`, `<=`, `>=`, `!=` — not an assignment.
      const prev = inner[eq - 1];
      const next = inner[eq + 1];
      if (next === '=' || next === '>' || prev === '=' || prev === '!' || prev === '<' || prev === '>') continue;

      let i = eq + 1;
      while (i < inner.length && /\s/.test(inner[i]!)) i++;
      if (inner[i] !== '{' && inner[i] !== '[') continue;

      const literal = extractBalancedLiteral(inner, i, MAX_LITERAL_BYTES);
      if (!literal) continue;
      candidates++;

      let parsed: unknown;
      try {
        parsed = JSON.parse(literal);
      } catch {
        continue;
      }

      const arrays = findPlayerArrays(parsed).sort((a, b) => b.length - a.length);
      for (const arr of arrays) {
        if (arr.length < minRows) continue;
        const cells = flattenObjectArray(arr);
        if (cells.length >= minRows) out.push(emptyBlock('json-embedded', out.length, cells));
      }
    }
  }

  return out;
}

/**
 * Walk forward from an opening `{`/`[`, tracking bracket depth and string
 * literals, and return the matching balanced substring (inclusive), or null
 * if it never balances within `maxBytes`.
 */
function extractBalancedLiteral(text: string, start: number, maxBytes: number): string | null {
  const close = text[start] === '{' ? '}' : ']';
  let depth = 0;
  let inString: '"' | "'" | null = null;

  for (let i = start; i < text.length && i - start < maxBytes; i++) {
    const c = text[i]!;
    if (inString) {
      if (c === '\\') { i++; continue; }
      if (c === inString) inString = null;
      continue;
    }
    if (c === '"' || c === "'") { inString = c; continue; }
    if (c === '{' || c === '[') depth++;
    else if (c === '}' || c === ']') {
      depth--;
      if (depth === 0) return c === close ? text.slice(start, i + 1) : null;
    }
  }
  return null;
}

/**
 * Recursively find arrays of plain objects anywhere in a parsed JSON value —
 * candidate ranking-list shapes. Bounded on depth and on the number of arrays
 * inspected, so a pathologically nested input cannot make this expensive.
 */
function findPlayerArrays(value: unknown, depth = 0, budget = { arrays: 0 }): unknown[][] {
  if (depth > MAX_TRAVERSAL_DEPTH || budget.arrays > MAX_ARRAYS_INSPECTED) return [];
  const found: unknown[][] = [];

  if (Array.isArray(value)) {
    budget.arrays++;
    const objectish = value.filter((v) => typeof v === 'object' && v !== null && !Array.isArray(v));
    if (value.length > 0 && objectish.length / value.length > 0.8) found.push(value);
    for (const item of value) found.push(...findPlayerArrays(item, depth + 1, budget));
  } else if (value && typeof value === 'object') {
    for (const v of Object.values(value)) found.push(...findPlayerArrays(v, depth + 1, budget));
  }

  return found;
}

/**
 * Turn an array of JSON objects into a cell grid. The column *set* is chosen
 * from whichever keys most of the sampled rows actually carry; what each
 * column *means* is still left entirely to `inferColumns`, exactly as for a
 * headerless CSV paste — this never assumes a field named `name` means name.
 */
function flattenObjectArray(arr: unknown[]): string[][] {
  const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

  const sample = arr.slice(0, 200).filter(isPlainObject);
  if (sample.length === 0) return [];

  const frequency = new Map<string, number>();
  const order: string[] = [];
  for (const obj of sample) {
    for (const key of Object.keys(obj)) {
      if (!frequency.has(key)) order.push(key);
      frequency.set(key, (frequency.get(key) ?? 0) + 1);
    }
  }

  const keys = order.filter((k) => (frequency.get(k) ?? 0) / sample.length >= 0.5).slice(0, MAX_JSON_COLUMNS);
  if (keys.length === 0) return [];

  return arr.filter(isPlainObject).map((obj) => keys.map((k) => scalarToString(obj[k])));
}

function scalarToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function cellText(fragment: string): string {
  return normalizeWhitespace(decodeEntities(fragment.replace(/<[^>]*>/g, ' ')))
    .replace(/\n+/g, ' ')
    .trim();
}

/**
 * Plain-text block detection.
 *
 * Two shapes are recognised, because they cover essentially every list a person
 * can paste: an explicitly numbered run ("1. Jahmyr Gibbs DET"), and a
 * delimited table (tabs from a spreadsheet, commas from a CSV, pipes from
 * Markdown or Discord).
 */
function findTextBlocks(text: string, minRows: number): CandidateBlock[] {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const out: CandidateBlock[] = [];

  // --- numbered runs -------------------------------------------------------
  // A run is a maximal stretch of lines starting with an integer. Unnumbered
  // lines inside a run (a section header like "RUNNING BACKS") break it, which
  // is correct: those are separate blocks and the scorer will pick the best.
  let run: string[] = [];
  let runIndex = 0;
  const flush = () => {
    if (run.length >= minRows) {
      out.push(emptyBlock('text-numbered', runIndex++, run.map(splitNumberedLine)));
    }
    run = [];
  };
  for (const line of lines) {
    if (/^\d{1,3}[.)\]:\s]/.test(line)) run.push(line);
    else flush();
  }
  flush();

  // --- delimited tables ----------------------------------------------------
  for (const delimiter of ['\t', '|', ','] as const) {
    const rows = lines
      .filter((l) => l.includes(delimiter))
      .map((l) => splitDelimited(l, delimiter));
    if (rows.length < minRows) continue;
    // Require a stable column count on most rows, otherwise this is prose that
    // happens to contain commas.
    const counts = new Map<number, number>();
    for (const r of rows) counts.set(r.length, (counts.get(r.length) ?? 0) + 1);
    const [modeLength, modeCount] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]!;
    if (modeLength < 2 || modeCount / rows.length < 0.6) continue;
    const consistent = rows.filter((r) => r.length === modeLength);
    if (consistent.length >= minRows) {
      out.push(emptyBlock('text-delimited', out.filter((b) => b.strategy === 'text-delimited').length, consistent));
    }
  }

  // --- whitespace-separated fallback --------------------------------------
  // A list with no delimiter and no numbers: one player per line. Only worth
  // trying when nothing else was found, since it matches almost any text.
  if (out.length === 0 && lines.length >= minRows) {
    out.push(emptyBlock('text-numbered', runIndex, lines.map((l) => [l])));
  }

  return out;
}

function splitNumberedLine(line: string): string[] {
  const m = /^(\d{1,3})[.)\]:\s]+(.*)$/.exec(line);
  if (!m) return splitTextRow(line);
  const rest = m[2]!.trim();
  return [m[1]!, ...splitTextRow(rest)];
}

/** Split a free-text row on tabs, pipes, or runs of 2+ spaces — never on a
 *  single space, which would shred "Amon-Ra St. Brown". */
function splitTextRow(text: string): string[] {
  if (text.includes('\t')) return splitDelimited(text, '\t');
  if (text.includes('|')) return splitDelimited(text, '|');
  if (/ {2,}/.test(text)) return text.split(/ {2,}/).map((s) => s.trim()).filter(Boolean);
  return [text.trim()];
}

function splitDelimited(line: string, delimiter: string): string[] {
  if (delimiter === ',') return splitCsvLine(line);
  return line
    .split(delimiter)
    .map((s) => s.trim())
    .filter((s, i, arr) => !(s === '' && (i === 0 || i === arr.length - 1)));
}

/** Comma splitting that respects quotes, so `"Smith, Jr.",RB` stays two cells. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
      continue;
    }
    if (c === '"') { inQuotes = true; continue; }
    if (c === ',') { out.push(field.trim()); field = ''; continue; }
    field += c;
  }
  out.push(field.trim());
  return out;
}

function emptyBlock(
  strategy: CandidateBlock['strategy'],
  index: number,
  cells: string[][],
): CandidateBlock {
  return {
    strategy,
    index,
    cells,
    columns: [],
    confidence: 0,
    rows: [],
    matched: 0,
    total: 0,
    had_header: false,
  };
}

// ---------------------------------------------------------------------------
// Column role inference — by content, not by header text (§4.8.2)
// ---------------------------------------------------------------------------

export function inferColumns(cells: string[][], index: PlayerIndex): ColumnRole[] {
  if (cells.length === 0) return [];
  const width = Math.max(...cells.map((r) => r.length));
  const body = dropHeaderRow(cells);
  const sample = body.slice(0, Math.min(body.length, 60));

  const roles: ColumnRole[] = new Array(width).fill('ignore');
  const scores: { col: number; role: ColumnRole; score: number }[] = [];

  for (let col = 0; col < width; col++) {
    const values = sample.map((r) => r[col] ?? '').filter((v) => v !== '');
    if (values.length === 0) continue;

    const teamShare = share(values, (v) => normalizeTeam(v) !== null);
    const posShare = share(values, (v) => normalizePosition(v) !== null);
    const intShare = share(values, (v) => /^\d{1,3}$/.test(v.trim()));
    const nameShare = share(values, (v) => looksLikeName(v));
    const byeShare = share(values, (v) => {
      const n = Number(v.trim());
      return Number.isInteger(n) && n >= 1 && n <= 18;
    });

    scores.push({ col, role: 'team', score: teamShare });
    scores.push({ col, role: 'position', score: posShare });
    scores.push({ col, role: 'name', score: nameShare * 1.05 });

    if (intShare > 0.8) {
      const nums = values.map((v) => Number(v.trim())).filter(Number.isFinite);
      const ascending = isMostlyAscending(nums);
      const distinct = new Set(nums).size;
      // A rank column counts up and is nearly all distinct. A tier column
      // counts up but repeats heavily. A bye column does neither.
      if (ascending && distinct / nums.length > 0.8) {
        scores.push({ col, role: 'rank', score: 0.95 });
      } else if (ascending && distinct <= Math.max(3, nums.length / 4)) {
        scores.push({ col, role: 'tier', score: 0.8 });
      } else if (byeShare > 0.9) {
        scores.push({ col, role: 'bye', score: 0.75 });
      }
    }
  }

  // Greedy assignment: strongest evidence wins its column, and each role is
  // claimed at most once. Ties fall to the leftmost column, which matches how
  // ranking tables are almost always laid out.
  scores.sort((a, b) => b.score - a.score || a.col - b.col);
  const usedRoles = new Set<ColumnRole>();
  const usedCols = new Set<number>();
  for (const { col, role, score } of scores) {
    if (score < 0.6) continue;
    if (usedCols.has(col) || usedRoles.has(role)) continue;
    roles[col] = role;
    usedCols.add(col);
    usedRoles.add(role);
  }

  // A single-column block is a name column by definition.
  if (width === 1) roles[0] = 'name';

  // No name column found but exactly one unclaimed column: it is the name.
  if (!roles.includes('name')) {
    const free = roles.findIndex((r) => r === 'ignore');
    if (free >= 0) roles[free] = 'name';
  }

  return roles;
}

function looksLikeName(value: string): boolean {
  const cleaned = stripAnnotations(value);
  if (cleaned.length < 3 || cleaned.length > 40) return false;
  if (/^\d+$/.test(cleaned)) return false;
  // Two or more word-ish tokens, mostly letters.
  const tokens = cleaned.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return false;
  const letters = cleaned.replace(/[^a-zA-Z]/g, '').length;
  return letters / cleaned.length > 0.6;
}

function share(values: string[], predicate: (v: string) => boolean): number {
  if (values.length === 0) return 0;
  return values.filter(predicate).length / values.length;
}

function isMostlyAscending(nums: number[]): boolean {
  if (nums.length < 3) return false;
  let ascents = 0;
  for (let i = 1; i < nums.length; i++) if (nums[i]! >= nums[i - 1]!) ascents++;
  return ascents / (nums.length - 1) > 0.85;
}

/**
 * Detect and drop a header row.
 *
 * Header text is *not* used to assign roles (that is the point of §4.8.2), but
 * it still has to be removed or it becomes an unmatched row that drags the
 * confidence score down.
 */
function dropHeaderRow(cells: string[][]): string[][] {
  const first = cells[0];
  if (!first || cells.length < 3) return cells;
  const headerish = first.filter((v) =>
    /^(rk|rank|overall|player|player name|name|pos|position|tm|team|tier|bye|notes|adp|proj|ecr)$/i.test(v.trim()),
  ).length;
  return headerish >= Math.max(1, Math.floor(first.length / 2)) ? cells.slice(1) : cells;
}

// ---------------------------------------------------------------------------
// Scoring (FR-46) — the confidence signal
// ---------------------------------------------------------------------------

function scoreBlock(block: CandidateBlock, index: PlayerIndex, matchThreshold: number): void {
  const withoutHeader = dropHeaderRow(block.cells);
  block.had_header = withoutHeader.length !== block.cells.length;

  const nameCol = block.columns.indexOf('name');
  const rankCol = block.columns.indexOf('rank');
  const teamCol = block.columns.indexOf('team');
  const posCol = block.columns.indexOf('position');
  const tierCol = block.columns.indexOf('tier');
  const byeCol = block.columns.indexOf('bye');
  const notesCol = block.columns.indexOf('notes');

  const rows: ExtractedRow[] = [];
  let matched = 0;

  withoutHeader.forEach((cell, i) => {
    const rawName = (nameCol >= 0 ? cell[nameCol] : cell[0]) ?? '';
    if (!rawName.trim()) return;

    const name = stripAnnotations(rawName);
    if (!name) return;

    // FR-47: rank comes from row order when there is no explicit rank column.
    const rank = rankCol >= 0 ? toInt(cell[rankCol]) ?? i + 1 : i + 1;
    const team = teamCol >= 0 ? normalizeTeam(cell[teamCol]) : inlineTeam(rawName);
    const position = posCol >= 0 ? normalizePosition(cell[posCol]) : inlinePosition(rawName);

    const result = index.match(name, { team, position }, matchThreshold);
    if (result.player_key) matched++;

    rows.push({
      rank,
      name,
      team,
      position,
      tier: tierCol >= 0 ? toInt(cell[tierCol]) : null,
      bye: byeCol >= 0 ? toInt(cell[byeCol]) : null,
      notes: notesCol >= 0 ? (cell[notesCol] ?? null) : null,
      player_key: result.player_key,
      suggestions: result.player_key ? [] : result.suggestions,
    });
  });

  block.rows = rows;
  block.matched = matched;
  block.total = rows.length;
  block.confidence = rows.length === 0 ? 0 : matched / rows.length;
}

/** Pull a team out of an unsplit name cell: "Jahmyr Gibbs DET". */
function inlineTeam(raw: string): string | null {
  const tokens = raw.split(/[\s,/|()[\]-]+/).filter(Boolean);
  for (let i = tokens.length - 1; i >= Math.max(0, tokens.length - 3); i--) {
    const team = normalizeTeam(tokens[i]!);
    if (team && team !== 'FA') return team;
  }
  return null;
}

function inlinePosition(raw: string): Position | null {
  const tokens = raw.split(/[\s,/|()[\]-]+/).filter(Boolean);
  for (let i = tokens.length - 1; i >= Math.max(0, tokens.length - 3); i--) {
    const token = tokens[i]!;
    const bare = normalizePosition(token);
    if (bare) return bare;
    const ranked = /^(QB|RB|WR|TE|K|PK|DEF|DST)\d{1,3}$/i.exec(token);
    if (ranked) return normalizePosition(ranked[1]!);
  }
  return null;
}

function toInt(value: string | undefined): number | null {
  if (value === undefined) return null;
  const n = parseInt(value.trim(), 10);
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Preview payload (FR-48)
// ---------------------------------------------------------------------------

export interface ExtractionPreview {
  strategy: CandidateBlock['strategy'];
  confidence: number;
  matched: number;
  total: number;
  columns: ColumnRole[];
  /** First 25 rows (FR-48). */
  head: ExtractedRow[];
  /** Last 10 rows (FR-48). */
  tail: ExtractedRow[];
  /** Every unmatched name, with suggestions (FR-48, FR-49). */
  unmatched: ExtractedRow[];
  /** True when confidence is below the configured threshold. */
  below_threshold: boolean;
}

/**
 * Build the mandatory, non-skippable first-import preview (FR-48).
 *
 * The preview is the safety mechanism for the whole BYOR feature: extraction is
 * heuristic, so nothing enters the ranking store until a human has looked at
 * the head, the tail, the count, the inferred mapping, the confidence, and
 * every unmatched name.
 */
export function buildPreview(block: CandidateBlock, threshold = 0.7): ExtractionPreview {
  return {
    strategy: block.strategy,
    confidence: block.confidence,
    matched: block.matched,
    total: block.total,
    columns: block.columns,
    head: block.rows.slice(0, 25),
    tail: block.rows.slice(-10),
    unmatched: block.rows.filter((r) => !r.player_key),
    below_threshold: block.confidence < threshold,
  };
}
